import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import {
    catchError,
    finalize,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    throwError,
} from 'rxjs';
import { User } from '../user/user.types';
import { LoginResponse } from './auth.types';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);

    private _infligthRefreshTokenRequest$: Observable<LoginResponse> | null =
        null;

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    set refreshToken(token: string) {
        localStorage.setItem('refreshToken', token);
    }

    get refreshToken(): string {
        return localStorage.getItem('refreshToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/users/forgot-password', { email });
    }

    /**
     * Reset password
     *
     * @param payload
     */
    resetPassword(payload: {
        email: string;
        token: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/users/reset-password', payload);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: {
        email: string;
        password: string;
        isLongLived: boolean;
    }): Observable<LoginResponse> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post('api/users/login', credentials).pipe(
            switchMap((response: any) => {
                // Store the access and refresh tokens in the local storage
                this.accessToken = response.accessToken;
                this.refreshToken = response.refreshToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    refreshAccessToken(): Observable<LoginResponse> {
        if (!this._infligthRefreshTokenRequest$) {
            this._infligthRefreshTokenRequest$ = this._httpClient
                .post<LoginResponse>('api/users/refresh', {
                    refreshToken: this.refreshToken,
                })
                .pipe(
                    switchMap((response: LoginResponse) => {
                        // Store the new access and refresh tokens in the local storage
                        this.accessToken = response.accessToken;
                        this.refreshToken = response.refreshToken;
                        // Set the authenticated flag to true
                        this._authenticated = true;
                        // Store the user on the user service
                        this._userService.user = response.user;
                        // Return a new observable with the response
                        return of(response);
                    }),
                    finalize(() => {
                        // Clear the inflight request
                        this._infligthRefreshTokenRequest$ = null;
                    }),
                    shareReplay(1)
                );
        }

        return this._infligthRefreshTokenRequest$;
    }

    /**
     * Sign in using the access token
     */
    getCurrentUser(): Observable<any> {
        // Sign in using the token
        // TODO
        return this._httpClient.get<User>('api/users/current').pipe(
            catchError(() =>
                // Return false
                of(false)
            ),
            switchMap((response: User) => {
                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response;

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // An expired access token is renewed rather than treated as signed out, so a reload
        // after the one-hour lifetime keeps the session. A 401 from the refresh itself is
        // handled by the auth interceptor, which signs the user out.
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return this.refreshToken
                ? this.refreshAccessToken().pipe(
                      map(() => true),
                      catchError(() => of(false))
                  )
                : of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.getCurrentUser();
    }
}
